import { useRef, useState } from 'react'
import { useEffectWithoutInitialRender } from './useEffectWithoutInitialRender'

/**
 * Debounces a search keyword and calls onSearch delayMs after the user stops typing.
 * Skips the initial render (so it doesn't fire on mount) and exposes an in-progress
 * flag for a loading indicator. Cancels the pending call if searchKeyword changes
 * again before delayMs elapses.
 *
 * Also returns flush(), which cancels the pending timer and runs onSearch immediately -
 * e.g. for an Enter-to-search shortcut. Without cancelling the timer first, the debounced
 * call would still fire later on its own schedule, producing a duplicate request.
 */
export function useDebouncedSearch(searchKeyword: string, onSearch: () => void, delayMs = 300) {
	const [searchInProgress, setSearchInProgress] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout>>()
	// Keep the latest onSearch in a ref so flush() always calls the current callback,
	// not a stale closure from whichever render scheduled the still-pending timer.
	const onSearchRef = useRef(onSearch)
	onSearchRef.current = onSearch

	useEffectWithoutInitialRender(() => {
		if (searchKeyword !== '') {
			setSearchInProgress(true)
		}

		const timer = setTimeout(() => {
			onSearchRef.current()
			setSearchInProgress(false)
			timerRef.current = undefined
		}, delayMs)
		timerRef.current = timer

		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchKeyword])

	const flush = () => {
		clearTimeout(timerRef.current)
		timerRef.current = undefined
		onSearchRef.current()
		setSearchInProgress(false)
	}

	return { searchInProgress, flush }
}
